import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { Collapse } from '@/components/Collapse'
import { Button } from '@/components/ui/button'
import { useAnalytics } from '@/provider/useAuth'
import { useTrackerHref } from '@/provider/useTrackerHref'
import FindingTypeInfoDialog from './FindingTypeInfoDialog'
import FixGuidance from './FixGuidance'
import RobotsPolicyMatrix from './RobotsPolicyMatrix'
import { comparePageUrls, pageLabel, pluralize } from './SiteHealthFormat'
import {
  CARD_CLASS,
  EYEBROW_CLASS,
  severityBadgeClass,
} from './SiteHealthStyles'
import type { FindingTypeDetailsProps } from './siteHealthComponents.types'

const COLLAPSED_FINDING_COUNT = 8

function countsForReport(report: FindingTypeDetailsProps['report']) {
  if (report.severityCounts) return report.severityCounts

  return {
    critical: report.findings.filter(
      (finding) => finding.severity === 'critical'
    ).length,
    warning: report.findings.filter((finding) => finding.severity === 'warning')
      .length,
    info: report.findings.filter((finding) => finding.severity === 'info')
      .length,
  }
}

function countLabel(report: FindingTypeDetailsProps['report']): string {
  const counts = countsForReport(report)
  const parts = [
    counts.critical > 0 ? `${counts.critical} critical` : null,
    counts.warning > 0 ? `${counts.warning} warning` : null,
    counts.info > 0 ? `${counts.info} info` : null,
  ].filter((part): part is string => part !== null)

  return parts.length > 0
    ? parts.join(' · ')
    : `${report.affectedPageCount} findings`
}

export default function FindingTypeDetails({
  auditId,
  report,
  expanded,
  onToggle,
}: FindingTypeDetailsProps) {
  const analytics = useAnalytics()
  const href = useTrackerHref()
  const [showAll, setShowAll] = useState(false)
  const sortedFindings = [...report.findings].sort((left, right) =>
    comparePageUrls(left.pageUrl ?? '', right.pageUrl ?? '')
  )
  const visibleFindings = showAll
    ? sortedFindings
    : sortedFindings.slice(0, COLLAPSED_FINDING_COUNT)
  const hiddenCount = report.findings.length - visibleFindings.length

  function toggle() {
    if (!expanded) {
      analytics.track('site_health_finding_expanded', {
        findingType: report.typeKey,
      })
    }

    onToggle()
  }

  return (
    <article
      className={`${CARD_CLASS} overflow-hidden ${expanded ? 'border-primary/40' : ''}`}
    >
      <div className="flex items-center gap-3 px-5 py-4">
        <Button
          type="button"
          variant="ghost"
          aria-expanded={expanded}
          onClick={toggle}
          className="flex h-auto min-w-0 items-center gap-3 rounded-none p-0 text-left hover:bg-transparent aria-expanded:bg-transparent"
        >
          {expanded ? (
            <ChevronUp
              className="size-4 shrink-0 text-primary"
              aria-hidden="true"
            />
          ) : (
            <ChevronDown
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold">{report.title}</span>
            {report.description && (
              <span className="block truncate text-sm font-normal text-muted-foreground">
                {report.description}
              </span>
            )}
          </span>
        </Button>
        <FindingTypeInfoDialog report={report} />
        <span className="flex-1" aria-hidden="true" />
        <span className="hidden shrink-0 text-sm tabular-nums text-muted-foreground sm:inline">
          {pluralize(report.affectedPageCount, 'page')}
        </span>
        <span className={`${severityBadgeClass(report.severity)} shrink-0`}>
          {countLabel(report)}
        </span>
      </div>

      <Collapse open={expanded}>
        <div className="space-y-5 border-t border-border bg-muted/10 p-5">
          <FixGuidance
            guidance={report}
            subject={{ findingType: report.typeKey }}
          />

          {report.robotsPolicy && (
            <RobotsPolicyMatrix policy={report.robotsPolicy} />
          )}

          {report.findings.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
              This finding type has no page-level evidence in the current
              result.
            </p>
          ) : (
            <div>
              <h3 className={EYEBROW_CLASS}>Affected pages</h3>
              <ul className="mt-3 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
                {visibleFindings.map((finding) => (
                  <li
                    key={finding.id}
                    className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {finding.pageUrl
                        ? pageLabel(finding.pageUrl)
                        : 'Site-wide'}
                    </span>
                    {finding.pageId && (
                      <Link
                        to={href(
                          `/site-health/${auditId}/pages/${finding.pageId}?finding=${finding.id}`
                        )}
                        className="shrink-0 text-sm font-medium text-primary underline-offset-4 hover:underline"
                      >
                        Inspect page
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
              {hiddenCount > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-3 text-primary"
                  onClick={() => setShowAll(true)}
                >
                  Show {pluralize(hiddenCount, 'more page')}
                </Button>
              )}
            </div>
          )}
        </div>
      </Collapse>
    </article>
  )
}
